import { get, writable } from 'svelte/store';
import { apiRootUrl } from './stores.svelte.js';
import { getAccessToken } from './account.svelte.js';
import { toastDisplay } from './utils.svelte.js';
import { t } from './i18n.svelte.js';
import { browser } from '$app/environment';


/**
 * @typedef {Object} OrgData
 * @property {OrgMember[]} members
 * @property {boolean} has_vault
 * @property {{logo: string, color: string}} assets
 * @property {string} support_email
 * @property {string} billing_email
 * @property {string} stripe_customer_id
 * @property {string} stripe_subscription_id
 * @property {string} stripe_subscription_item_id
 * @property {string} stripe_subscription_status
 * @property {string} last_billing_sync
 */

/**
 * @typedef {Object} OrgMember
 * @property {string} user_id
 * @property {string} email
 * @property {"admin" | "member" | "viewer"} role
 * @property {string} joined_at
 */


/**
 * @typedef {Object} PublicOrgMember
 * @property {string} user_id
 * @property {string} email
 * @property {"admin" | "member" | "viewer"} role
 * @property {string} joined_at
 * @property {string} display_name
 * @property {string} photo_url
 */

/**
 * @typedef {Object} OrgInvite
 * @property {string} invite_token
 * @property {string} email
 * @property {"admin" | "member" | "viewer"} role
 * @property {string} invited_at
 */


/**
 * @typedef {Object} Organization
 * @property {string} id - The unique identifier of the organization.
 * @property {string} name - The name of the organization.
 * @property {string} description - The description of the organization.
 * @property {string} created_at - The creation timestamp of the organization.
 * @property {Object} org_data - Organization data.
 * @property {Array<{user_id: string, role: string, joined_at: string}>} org_data.members - Members of the organization.
 * @property {boolean} org_data.has_vault - Whether the organization has a vault.
 * @property {Object} org_data.assets - Assets of the organization.
 * @property {string} org_data.assets.logo - The URL of the organization's logo.
 * @property {string} org_data.assets.color - The color of the organization.
 * @property {string} org_data.support_email - The support email of the organization.
 * @property {string} org_data.billing_email - The billing email of the organization.
 * @property {string} org_data.stripe_customer_id - The Stripe customer ID.
 * @property {string} org_data.stripe_subscription_id - The Stripe subscription ID.
 * @property {string} org_data.stripe_subscription_item_id - The Stripe subscription item ID.
 * @property {string} org_data.stripe_subscription_status - The subscription status.
 * @property {string} org_data.last_billing_sync - The last billing sync timestamp.
 */

/** @type {import('svelte/store').Writable<Organization|null>} */
export const userOrg = writable(
    (browser &&
        (localStorage.getItem('userOrg') == null
            ? null
            : (() => {
                try {
                    return JSON.parse(localStorage?.getItem('userOrg') || 'null');
                } catch {
                    return null;
                }
            })())) ||
    null
);
userOrg.subscribe((val) => browser && localStorage.setItem('userOrg', JSON.stringify(val)));


/**
 * @param {unknown} error
 * @returns {never}
 */
const handleOrgError = (error) => {
    console.error('Organization operation failed:', error);
    toastDisplay(t('error.generalErrorMsg'), 'error');
    throw error instanceof Error ? error : new Error(String(error));
};

/**
 * Crée une organisation.
 * @param {string} name
 * @param {string} description
 * @param {string} email
 * @param {File} [logo]
 * @param {string} [color]
 * @returns {Promise<Organization>}
 */
export const createOrg = async (name, description, email, logo, color) => {
    const token = await getAccessToken()
    const form = new FormData()
    form.append('name', name)
    form.append('description', description)
    form.append('email', email)
    color && form.append('color', color)
    logo && form.append('logo', logo)

    const res = await fetch(`${get(apiRootUrl)}/v3/orgs/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
    })
    if (!res.ok) throw new Error('Failed to create organization')
    const org = await res.json()
    userOrg.set(org)
    return org
}

/**
 * @param {string} orgId
 * @returns {Promise<Organization | undefined>}
 */
export const getOrg = async (orgId) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/`, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!response.ok) throw new Error('Failed to fetch organization');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * Met à jour une organisation.
 *
 * @param {string} orgId
 * @param {string} name
 * @param {string} description
 * @param {Object} [options]
 * @param {string} [options.billingEmail]
 * @param {File} [options.logo]
 * @param {string} [options.color]
 * @returns {Promise<Organization>}
 */
export const updateOrg = async (
    orgId,
    name,
    description,
    { billingEmail, logo, color } = {}
) => {
    const token = await getAccessToken()
    const form = new FormData()
    form.append('name', name)
    form.append('description', description)
    billingEmail && form.append('billingEmail', billingEmail)
    color && form.append('color', color)
    logo && form.append('logo', logo)

    const res = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
    })

    if (!res.ok) throw new Error('Failed to update organization')
    const org = await res.json()
    userOrg.set(org)
    return org
}

/**
 * @param {string} orgId
 * @param {string} userId
 * @param {"admin" | "member" | "viewer"} role
 * @returns {Promise<Organization | undefined>}
 */
export const addMember = async (orgId, userId, role) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/members/add/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ user_id: userId, role })
        });
        if (!response.ok) throw new Error('Failed to add member');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} orgId
 * @param {string} userId
 * @returns {Promise<Organization | undefined>}
 */
export const removeMember = async (orgId, userId) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/members/remove/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ user_id: userId })
        });
        if (!response.ok) throw new Error('Failed to remove member');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} orgId
 * @param {string} userId
 * @param {"admin" | "member" | "viewer"} role
 * @returns {Promise<Organization | undefined>}
 */
export const changeRole = async (orgId, userId, role) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/members/role/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ user_id: userId, role })
        });
        if (!response.ok) throw new Error('Failed to change role');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} orgId
 * @param {string} email
 * @param {"admin" | "member" | "viewer"} role
 * @returns {Promise<OrgInvite | undefined>}
 */
export const inviteMember = async (orgId, email, role) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/invites/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ email, role })
        });
        if (!response.ok) throw new Error('Failed to send invite');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} token
 * @returns {Promise<void>}
 */
export const acceptInvite = async (token) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/invites/accept/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ token })
        });
        if (!response.ok) throw new Error('Failed to accept invite');
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} orgId
 * @returns {Promise<string | undefined>}
 */
export const getBillingPortalUrl = async (orgId) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/billing/`, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!response.ok) throw new Error('Failed to get billing portal URL');
        const { url } = await response.json();
        return url;
    } catch (error) {
        handleOrgError(error);
    }
};

/**
 * @param {string} orgId
 * @returns {Promise<PublicOrgMember[] | undefined>}
 */
export const getOrgMembers = async (orgId) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/members/`, {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (!response.ok) throw new Error('Failed to fetch organization members');
        return await response.json();
    } catch (error) {
        handleOrgError(error);
    }
};




/**
 * @param {string} cssproperty
 * @param {string} textcolor
 * @returns {string}
 */
export const orgColorStyle = (cssproperty, textcolor) => {
    const org = get(userOrg);
    if (org?.org_data?.assets?.color) {
        return `${cssproperty}: ${org.org_data.assets.color}; color: ${textcolor}!important;`;
    }
    return `${cssproperty}: #AAD5FC; color: black!important;`;
};

/**
 * @returns {string | null}
 */
export const orgLogo = () => {
    const org = get(userOrg);
    return org?.org_data?.assets?.logo || null;
};

/**
 * @param {string} email
 * @returns {string | null}
 */
export const companyLogoFromEmail = (email) => {
    try {
        if (!email) return null;

        const domain = email.split('@')[1];
        switch (domain) {
            case 'totalenergies.com':
                return 'https://upload.wikimedia.org/wikipedia/fr/thumb/f/f7/Logo_TotalEnergies.svg/langfr-2560px-Logo_TotalEnergies.svg.png';
            default:
                return null;
        }
    } catch (error) {
        console.error(error);
        return null;
    }
};



/**
 * Deletes an organization
 * @param {string} orgId - The ID of the organization to delete
 * @returns {Promise<boolean>} - True if successful, throws error otherwise
 */
export const deleteOrg = async (orgId) => {
    try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to delete organization: ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error('Error deleting organization:', error);
        throw error;
    }
};

/**
 * Start organization subscription or return Stripe checkout URL if no payment method on file
 * @param {string} orgId
 * @param {"monthly" | "yearly"} billingPeriod
 * @returns {Promise<any>} stripe checkout or Organization
 */
export const subscribeOrg = async (orgId, billingPeriod = 'monthly') => {
    const token = await getAccessToken();
    const res = await fetch(`${get(apiRootUrl)}/v3/orgs/${orgId}/subscribe/?billing_period=${billingPeriod}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to start subscription');
    }
    const payload = await res.json();
    if (payload.url) return payload;
    userOrg.set(payload);
    return payload;
};
